import { Component, OnInit, ViewChild, Input, Output, EventEmitter, TrackByFunction } from '@angular/core'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { Store, select } from '@ngrx/store'
import {
  ConfirmDialogService,
  untilDestroy,
  isEmptyTable,
  localeCompareSort,
  isSomething,
  FilterFormGroup,
  SelectableRow,
} from '@tokeet-frontend/tv3-platform'
import {
  ArchiveAutomation,
  AutomationTypes,
  AutomationsTable,
  AutomationViewGroup,
  DeleteAutomation,
  LoadAutomations,
  PauseAutomation,
  selectAutomationsLoaded,
  UnArchiveAutomation,
  UnPauseAutomation,
} from '@tokeet-frontend/automations'
import { FilterGroup } from '@tv3/services/utils/filters.service'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { map, filter, take } from 'rxjs/operators'
import { ActivatedRoute } from '@angular/router'
import { AutomationsFilter } from './automations-table.filter'
import { Observable } from 'rxjs'
import * as lodash from 'lodash'

@Component({
  selector: 'app-automations-table',
  templateUrl: './automations-table.component.html',
  styleUrls: ['./automations-table.component.scss'],
  providers: [AutomationsFilter],
})
export class AutomationsTableComponent extends AutomationsTable<AutomationViewGroup> implements OnInit {
  @Input() viewType: 'list' | 'grid' = 'grid'
  @Input() type: AutomationTypes | 'all' = 'all'
  @Input() showActions = false

  types = AutomationTypes

  get isDuplicable() {
    return this.type !== AutomationTypes.Message && this.duplicate.observers.length > 0
  }

  get isEditable() {
    return this.edit.observers.length > 0
  }

  @Output() add = new EventEmitter()
  @Output() edit = new EventEmitter<AutomationViewGroup>()
  @Output() rowClick = new EventEmitter<AutomationViewGroup>()
  @Output() duplicate = new EventEmitter<AutomationViewGroup>()

  @ViewChild('paginator') set paginator(p: MatPaginator) {
    this.dataSource.paginator = p
  }
  @ViewChild(MatSort) set sort(s: MatSort) {
    this.dataSource.sort = s
    this.applySorting()
  }

  displayedColumns = ['select', 'created', 'name', 'event', 'handler', 'lastupdate', 'status', 'edit']

  filterGroup = FilterGroup.Automations
  isEmptyTable$ = isEmptyTable(this.dataSource)
  isLoading$ = this.store.pipe(
    select(selectAutomationsLoaded),
    map((loaded) => !loaded)
  )

  isEmpty$: Observable<boolean>
  filters: FilterFormGroup<AutomationViewGroup>

  @Input() tableType = TableType.Automations
  self: AutomationsTableComponent

  constructor(
    private route: ActivatedRoute,
    private store: Store<any>,
    private confirmDialog: ConfirmDialogService,
    public automationFilters: AutomationsFilter
  ) {
    super()
    this.self = this
  }
  trackBy: TrackByFunction<AutomationViewGroup> = (index, automation) => automation.group_id

  ngOnInit() {
    this.automationFilters.setAutomationType(this.type)
    this.isEmpty$ = this.automationFilters.group.origin.pipe(map((automations) => !automations?.length))
    this.filters = this.automationFilters.group

    this.dataSource.sortData = localeCompareSort

    this.route.queryParams.pipe(filter(isSomething), take(1)).subscribe((params) => {
      if (isSomething(params['events'])) {
        this.filters.reset({ events: params['events'] })
      }
    })

    this.automationFilters.group.changes.pipe(untilDestroy(this)).subscribe((automations) => {
      this.dataSource.data = automations
      this.applySorting()
    })
  }

  applySorting() {
    if (this.viewType === 'grid') {
      this.dataSource.sort = null // Disable MatSort to avoid conflicts
      this.dataSource.data = this.dataSource.data.slice().sort((a, b) => b.lastupdate - a.lastupdate)
    }
  }

  onRefresh() {
    this.store.dispatch(LoadAutomations({}))
  }

  onSearch(term: string) {
    this.dataSource.paginator?.firstPage()
    this.dataSource.filter = (term + '').trim().toLowerCase()
  }

  onAdd() {
    this.add.emit()
  }

  onRowClick(automation: AutomationViewGroup) {
    this.rowClick.emit(automation)
    this.onEdit(automation)
  }

  onEdit(automation: AutomationViewGroup) {
    if (!this.isEditable) return
    this.edit.emit(automation)
  }

  onDuplicate(automation: AutomationViewGroup) {
    this.duplicate.emit(automation)
  }

  onTogglePause(automation: AutomationViewGroup) {
    if (automation.status === 1) {
      this.store.dispatch(PauseAutomation({ ids: automation.ids }))
    } else {
      this.store.dispatch(UnPauseAutomation({ ids: automation.ids }))
    }
  }

  onArchive(automation: AutomationViewGroup) {
    if (!!automation.status) {
      this.confirmDialog
        .confirm({
          title: $localize`Pause automation?`,
          body: $localize`Archiving the automation also pauses it. Are you sure?`,
          confirmText: $localize`Pause & Archive`,
          cancelText: $localize`Cancel`,
        })
        .subscribe(() => {
          this.store.dispatch(PauseAutomation({ ids: automation.ids }))
          this.store.dispatch(ArchiveAutomation({ ids: automation.ids }))
        })
    } else {
      this.store.dispatch(ArchiveAutomation({ ids: automation.ids }))
    }
  }

  onUnArchive(automation: AutomationViewGroup) {
    this.store.dispatch(UnArchiveAutomation({ ids: automation.ids }))
  }

  onDelete(automation: AutomationViewGroup) {
    this.confirmDialog
      .confirm({
        title: $localize`Delete this automation?`,
        body: $localize`Are you sure you want to delete this automation?`,
      })
      .subscribe(() => {
        this.store.dispatch(DeleteAutomation({ ids: automation.ids }))
      })
  }

  onArchiveSelected() {
    const items = lodash.flatten<string>(
      this.getSelected()
        .filter((t) => !t.archived)
        .map((t) => t.ids)
    )
    if (!items.length) {
      return
    }
    this.confirmDialog
      .confirm({
        title: $localize`Pause automations?`,
        body: $localize`Archiving the automations also pauses them. Are you sure?`,
        confirmText: $localize`Pause & Archive`,
        cancelText: $localize`Cancel`,
      })
      .subscribe(() => {
        this.store.dispatch(PauseAutomation({ ids: items }))
        this.store.dispatch(ArchiveAutomation({ ids: items }))
      })
  }

  onUnarchiveSelected() {
    const items = lodash.flatten<string>(
      this.getSelected()
        .filter((t) => !!t.archived)
        .map((t) => t.ids)
    )

    if (!items.length) {
      return
    }

    this.store.dispatch(UnArchiveAutomation({ ids: items }))
  }

  onPauseSelected() {
    const items = lodash.flatten<string>(
      this.getSelected()
        .filter((t) => !!t.status)
        .map((t) => t.ids)
    )
    if (!items.length) {
      return
    }
    this.confirmDialog
      .confirm({
        title: $localize`Pause selected automations?`,
        body: $localize`Are you sure you want to pause the selected automations?`,
      })
      .subscribe(() => {
        this.store.dispatch(PauseAutomation({ ids: items }))
      })
  }

  onResumeSelected() {
    const items = lodash.flatten<string>(
      this.getSelected()
        .filter((t) => !t.status)
        .map((t) => t.ids)
    )

    if (!items.length) {
      return
    }
    this.confirmDialog
      .confirm({
        title: $localize`Resume selected automations?`,
        body: $localize`Are you sure you want to resume the selected automations?`,
      })
      .subscribe(() => {
        this.store.dispatch(UnPauseAutomation({ ids: items }))
      })
  }

  onDeleteSelected() {
    const items = lodash.flatten<string>(this.getSelected('ids'))
    if (!items.length) {
      return
    }
    this.confirmDialog
      .confirm({
        title: $localize`Delete selected automations?`,
        body: $localize`Are you sure you want to delete the selected automations?`,
      })
      .subscribe(() => {
        this.store.dispatch(DeleteAutomation({ ids: items }))
      })
  }
}
